import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "react-query";
import { SessionExpirationModal } from "../AuthProvider";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";
import { ErrorBoundary } from "react-error-boundary";
import { AxiosError, isAxiosError } from "axios";
import { usePostHog } from "posthog-js/react";
import { CLIENT_ERROR_KIND_TAG } from "@/lib/clientErrorReporter";
import { cachedActiveGithubOrg, cachedUserId, errorAnalyticsProperties } from "@/lib/errorReporting";
import { CORE_ANALYTICS_EVENTS } from "@/lib/posthog-constants";
interface BSErrorBoundaryProps {
  children: React.ReactNode;
  title: string;
  FallbackComponent?: React.ComponentType<{
    error: any;
  }>;
  fallback?: React.ReactNode | ((props: {
    error: any;
    resetErrorBoundary: () => void;
  }) => React.ReactNode);
  /** Leaves an error state when any entry changes, like react-error-boundary. */
  resetKeys?: unknown[];
  onReset?: () => void;
  /** Once per caught error, after it is reported. */
  onError?: (error: Error) => void;
  /** Tags on the Sentry event, e.g. `{ feature: "diff_viewer" }`; a function derives them from the caught error. */
  sentryTags?: SentryTags | ((error: Error) => SentryTags);
}
type SentryTags = Record<string, string>;
const DefaultFallback = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-[300px] border border-dashed rounded-lg flex items-center justify-center text-neutral-300 dark:text-neutral-600 text-sm">Something went wrong, please try refreshing the page</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export function BSErrorBoundary(t0) {
  const $ = _c(17);
  const {
    children,
    title,
    FallbackComponent: t1,
    fallback,
    resetKeys,
    onReset,
    onError,
    sentryTags
  } = t0;
  const FallbackComponent = t1 === undefined ? DefaultFallback : t1;
  const queryClient = useQueryClient();
  const postHog = usePostHog();
  let t2;
  if ($[0] !== onError || $[1] !== postHog || $[2] !== queryClient || $[3] !== sentryTags || $[4] !== title) {
    t2 = error => {
      const activeGithubOrg = cachedActiveGithubOrg(queryClient);
      const userId = cachedUserId(queryClient);
      const tags = typeof sentryTags === "function" ? sentryTags(error) : sentryTags;
      Sentry.withScope(scope => {
        if (userId !== null) {
          scope.setUser({
            id: userId
          });
        }
        if (activeGithubOrg) {
          scope.setTag("organization", activeGithubOrg);
          scope.setExtra("activeGithubOrg", activeGithubOrg);
        }
        if (!isAxiosError(error)) {
          scope.setTag(CLIENT_ERROR_KIND_TAG, "render_crash");
        }
        for (const [tag, value] of Object.entries(tags ?? {})) {
          scope.setTag(tag, value);
        }
        scope.setExtra("errorBoundaryTitle", title);
        const errorWithTitle = new Error(`[${title}] ${error.message}`);
        errorWithTitle.stack = error.stack;
        Sentry.captureException(errorWithTitle);
      });
      console.error(`[${title}] error boundary triggered with error`, error);
      postHog?.capture(CORE_ANALYTICS_EVENTS.ERROR, {
        ...errorAnalyticsProperties(error),
        errorBoundaryTitle: title,
        orgName: activeGithubOrg
      });
      onError?.(error);
    };
    $[0] = onError;
    $[1] = postHog;
    $[2] = queryClient;
    $[3] = sentryTags;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const report = t2;
  let t3;
  if ($[6] !== FallbackComponent || $[7] !== fallback) {
    t3 = (error_0, resetErrorBoundary) => {
      if (error_0 instanceof AxiosError && error_0.response?.status === 401) {
        return <SessionExpirationModal open={true} setOpen={_temp} />;
      }
      if (fallback !== undefined) {
        if (typeof fallback === "function") {
          return fallback({
            error: error_0,
            resetErrorBoundary
          });
        }
        return fallback;
      }
      return <FallbackComponent error={error_0} />;
    };
    $[6] = FallbackComponent;
    $[7] = fallback;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const renderFallback = t3;
  let t4;
  if ($[9] !== renderFallback) {
    t4 = t5 => {
      const {
        error: error_1,
        resetErrorBoundary: resetErrorBoundary_0
      } = t5;
      return renderFallback(error_1, resetErrorBoundary_0);
    };
    $[9] = renderFallback;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== children || $[12] !== onReset || $[13] !== report || $[14] !== resetKeys || $[15] !== t4) {
    t5 = <ErrorBoundary onError={report} fallbackRender={t4} resetKeys={resetKeys} onReset={onReset}>{children}</ErrorBoundary>;
    $[11] = children;
    $[12] = onReset;
    $[13] = report;
    $[14] = resetKeys;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
function _temp() {}